import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import {
  getFlashingEffectsEnabled,
  getVisitorBackgroundMusicMuted,
  setFlashingEffectsEnabled as persistFlashingEffectsEnabled,
  setVisitorBackgroundMusicMuted as persistVisitorBackgroundMusicMuted,
} from "../utils/visitorPreferences";

type GalleryChromeContextValue = {
  isPseudoMoMGallery: boolean;
  setIsPseudoMoMGallery: (value: boolean) => void;
  hasPlayableGalleryBackgroundMusic: boolean;
  setHasPlayableGalleryBackgroundMusic: (value: boolean) => void;
  visitorBackgroundMusicMuted: boolean;
  setVisitorBackgroundMusicMuted: (value: boolean) => void;
  flashingEffectsEnabled: boolean;
  setFlashingEffectsEnabled: (value: boolean) => void;
};

const defaultGalleryChromeContext: GalleryChromeContextValue = {
  isPseudoMoMGallery: false,
  setIsPseudoMoMGallery: () => {},
  hasPlayableGalleryBackgroundMusic: false,
  setHasPlayableGalleryBackgroundMusic: () => {},
  visitorBackgroundMusicMuted: false,
  setVisitorBackgroundMusicMuted: () => {},
  flashingEffectsEnabled: true,
  setFlashingEffectsEnabled: () => {},
};

const GalleryChromeContext = createContext<GalleryChromeContextValue>(defaultGalleryChromeContext);

type GalleryChromeProviderProps = {
  children: ReactNode;
  initialIsPseudoMoMGallery?: boolean;
  initialHasPlayableGalleryBackgroundMusic?: boolean;
};

export const GalleryChromeProvider = ({
  children,
  initialIsPseudoMoMGallery = false,
  initialHasPlayableGalleryBackgroundMusic = false,
}: GalleryChromeProviderProps) => {
  const [isPseudoMoMGallery, setIsPseudoMoMGallery] = useState(initialIsPseudoMoMGallery);
  const [hasPlayableGalleryBackgroundMusic, setHasPlayableGalleryBackgroundMusic] = useState(
    initialHasPlayableGalleryBackgroundMusic
  );
  const [visitorBackgroundMusicMuted, setVisitorBackgroundMusicMutedState] = useState(
    getVisitorBackgroundMusicMuted
  );
  const [flashingEffectsEnabled, setFlashingEffectsEnabledState] = useState(
    getFlashingEffectsEnabled
  );
  const setVisitorBackgroundMusicMuted = useCallback((value: boolean) => {
    persistVisitorBackgroundMusicMuted(value);
    setVisitorBackgroundMusicMutedState(value);
  }, []);
  const setFlashingEffectsEnabled = useCallback((value: boolean) => {
    persistFlashingEffectsEnabled(value);
    setFlashingEffectsEnabledState(value);
  }, []);
  const value = useMemo(
    () => ({
      isPseudoMoMGallery,
      setIsPseudoMoMGallery,
      hasPlayableGalleryBackgroundMusic,
      setHasPlayableGalleryBackgroundMusic,
      visitorBackgroundMusicMuted,
      setVisitorBackgroundMusicMuted,
      flashingEffectsEnabled,
      setFlashingEffectsEnabled,
    }),
    [
      flashingEffectsEnabled,
      hasPlayableGalleryBackgroundMusic,
      isPseudoMoMGallery,
      setFlashingEffectsEnabled,
      setVisitorBackgroundMusicMuted,
      visitorBackgroundMusicMuted,
    ]
  );

  return (
    <GalleryChromeContext.Provider value={value}>
      {children}
    </GalleryChromeContext.Provider>
  );
};

export const useGalleryChromeContext = () => useContext(GalleryChromeContext);
